(function (global) {
  const resolvePurchaseOptionState = (dataset = {}, acknowledgmentChecked = false) => {
    const isSubscription = dataset.subscription !== 'false';
    const planValid =
      dataset.planValid === 'true' &&
      Boolean(dataset.variantId && dataset.productId && dataset.productUrl);
    const available = dataset.available === 'true';
    const variantId = planValid ? dataset.variantId || '' : '';
    const productId = planValid ? dataset.productId || '' : '';
    const productUrl = planValid ? dataset.productUrl || '' : '';
    const sellingPlanId = isSubscription && planValid ? dataset.sellingPlanId || '' : '';

    return {
      available,
      buttonDisabled:
        !planValid ||
        !available ||
        (isSubscription && !sellingPlanId) ||
        (isSubscription && !acknowledgmentChecked),
      isSubscription,
      planValid,
      productId,
      productUrl,
      sellingPlanDisabled: !isSubscription || !planValid || !available || !sellingPlanId,
      sellingPlanId,
      variantDisabled: !planValid || !available || !variantId,
      variantId,
    };
  };

  class BrPdpPurchaseOptions extends (global.HTMLElement || class {}) {
    abortController = new AbortController();

    connectedCallback() {
      if (this.abortController.signal.aborted) this.abortController = new AbortController();

      const { signal } = this.abortController;
      this.querySelectorAll('.br-pdp-main__plan-input').forEach((input) => {
        input.addEventListener('change', this.handlePlanChange, { signal });
      });
      this.querySelectorAll('.br-pdp-main__plan').forEach((plan) => {
        plan.addEventListener('click', this.handlePlanClick, { signal });
      });
      this.querySelector('[data-ack-checkbox]')?.addEventListener('change', this.syncPlanState, {
        signal,
      });

      this.querySelectorAll('[data-dose]').forEach((button) => {
        button.addEventListener('click', this.handleDoseChange, { signal });
      });
      this.syncPlanState();
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    getSelectedPlan() {
      return this.querySelector('.br-pdp-main__plan-input:checked')?.closest('.br-pdp-main__plan');
    }

    handlePlanChange = (event) => {
      if (!event.currentTarget.checked) return;
      this.syncPlanState(true);
    };

    handleDoseChange = (event) => {
      const dose = event.currentTarget.dataset.dose;
      const previous = this.getSelectedPlan();
      const plans = [...this.querySelectorAll('[data-dose-group]')];
      const group = plans.filter((plan) => plan.dataset.doseGroup === dose);
      const next =
        group.find(
          (plan) =>
            plan.dataset.supplyDays === previous?.dataset.supplyDays &&
            plan.dataset.subscription === previous?.dataset.subscription
        ) || group[0];
      plans.forEach((plan) => {
        plan.hidden = plan.dataset.doseGroup !== dose;
        plan.querySelector('.br-pdp-main__plan-input').checked = plan === next;
      });
      this.querySelectorAll('[data-dose]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.dose === dose));
      });
      this.syncPlanState(true);
      next
        ?.querySelector('.br-pdp-main__plan-input')
        .dispatchEvent(new Event('change', { bubbles: true }));
    };

    /* ── VSL scroll-root repair ──────────────────────────────────────
       On the VSL pages `.page-wrapper` owns scrolling and
       `.br-pdp-main__info` is `position: static`, so focusing the radio
       inside a plan card can scroll the DOCUMENT as well. We let the
       native label -> radio activation run untouched (so `change` fires
       once, keyboard selection is unaffected, and any other listener on
       the input still sees the event), then restore whatever document
       scroll offset was in effect immediately before the click.

       Restoring the CAPTURED offset rather than forcing 0 is deliberate:
       if the document legitimately holds a scroll position, forcing 0
       would yank the customer to the top of the page mid-purchase.

       SUSPECTED ROOT CAUSE, not yet confirmed in a browser:
       `.br-pdp-main__plan-input` is `position: absolute` while
       `.br-pdp-main__plan` is not positioned, so the input's containing
       block comes from `.br-pdp-main__info` — which this page sets to
       `static`. Adding `position: relative` to the plan card would very
       likely remove the need for this handler entirely. Verify in a
       browser before deleting any of it.
       ---------------------------------------------------------------- */
    handlePlanClick = () => {
      if (!this.isVslDesktopPurchaseOptions()) return;
      this.queueVslScrollRootRepair();
    };

    isVslDesktopPurchaseOptions = () =>
      Boolean(
        this.closest('.br-pdp-main--vsl-offer') &&
        global.matchMedia?.('(min-width: 900px)').matches &&
        document.querySelector('.page-wrapper') instanceof HTMLElement
      );

    queueVslScrollRootRepair = () => {
      if (!this.isVslDesktopPurchaseOptions()) return;

      const target = global.scrollY || document.documentElement.scrollTop || 0;

      const restoreDocumentScroll = () => {
        if (
          global.scrollY === target &&
          document.documentElement.scrollTop === target &&
          document.body.scrollTop === target
        ) {
          return;
        }

        try {
          global.scrollTo({ top: target, left: 0, behavior: 'instant' });
        } catch (_error) {
          global.scrollTo(0, target);
        }
        document.documentElement.scrollTop = target;
        document.body.scrollTop = target;
      };

      if (typeof global.requestAnimationFrame === 'function') {
        global.requestAnimationFrame(() => {
          restoreDocumentScroll();
          global.requestAnimationFrame(restoreDocumentScroll);
        });
      } else {
        global.setTimeout(restoreDocumentScroll, 0);
      }
    };

    syncPlanState = (dispatchVariantChange = false) => {
      const plan = this.getSelectedPlan();
      const acknowledgment = this.querySelector('[data-ack-checkbox]');
      const state = resolvePurchaseOptionState(
        plan?.dataset,
        acknowledgment ? acknowledgment.checked : true
      );
      const variantInput = this.querySelector('[ref="variantId"]');
      const sellingPlanInput = this.querySelector('[data-selling-plan-input]');
      const productForm = this.querySelector('product-form-component');
      const form = this.querySelector('.br-pdp-main__form');
      const ctaButton = this.querySelector('[ref="addToCartButton"]');
      const ctaText = ctaButton?.querySelector('.add-to-cart-text__content > span > span');
      const acknowledgmentRow = this.querySelector('[data-ack-row]');
      const renewalCaption = this.querySelector('[data-renew-caption]');

      const variantChanged = variantInput && variantInput.value !== state.variantId;
      if (variantInput) {
        variantInput.value = state.variantId;
        variantInput.disabled = state.variantDisabled;
      }

      if (sellingPlanInput) {
        sellingPlanInput.value = state.sellingPlanId;
        sellingPlanInput.disabled = state.sellingPlanDisabled;
      }

      if (productForm) {
        productForm.dataset.productId = state.productId;
        productForm.dataset.productUrl = state.productUrl;
      }

      // Consumers must observe the new variant, selling plan and product together.
      if (dispatchVariantChange && variantChanged) {
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (acknowledgmentRow && acknowledgment) {
        acknowledgmentRow.classList.toggle('is-hidden', !state.isSubscription);
        acknowledgment.required = state.isSubscription;
        acknowledgment.disabled = !state.isSubscription;
      }

      if (ctaButton) ctaButton.disabled = state.buttonDisabled;

      if (ctaText && plan) {
        const baseCta = form?.dataset.baseCta || '';
        const suffix = plan.dataset.ctaSuffix || '';
        ctaText.textContent =
          this.dataset?.expandedOffers !== undefined
            ? state.isSubscription
              ? this.dataset.subscribeLabel
              : this.dataset.onceLabel
            : suffix
              ? `${baseCta} — ${suffix}`
              : baseCta;
      }

      this.querySelector('[data-offer-cancel]')?.classList.toggle(
        'is-hidden',
        !state.isSubscription
      );

      if (renewalCaption) {
        const renewalText = plan?.dataset.renewText || '';
        renewalCaption.textContent = renewalText;
        renewalCaption.classList.toggle('is-hidden', renewalText === '');
      }
    };
  }

  global.brPdpPurchaseOptions = Object.freeze({
    BrPdpPurchaseOptions,
    resolvePurchaseOptionState,
  });

  if (global.customElements && !global.customElements.get('br-pdp-purchase-options')) {
    global.customElements.define('br-pdp-purchase-options', BrPdpPurchaseOptions);
  }
})(globalThis);
